"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLoserBracketRoundName = exports.getWinnerBracketRoundName = exports.getRoundName = exports.getBracketName = exports.getGroupName = exports.getMatchStatus = exports.getFinalMatchLabel = exports.getMatchLabel = exports.getFinalOriginHint = exports.getOriginHint = exports.toI18nKey = exports.t = exports.addLocale = exports.locales = void 0;
const i18next_1 = __importDefault(require("i18next"));
const i18next_browser_languagedetector_1 = __importDefault(require("i18next-browser-languagedetector"));
const brackets_model_1 = require("brackets-model");
const helpers_1 = require("./helpers");
const translation_json_1 = __importDefault(require("./i18n/en/translation.json"));
const translation_json_2 = __importDefault(require("./i18n/fr/translation.json"));
exports.locales = {
    en: translation_json_1.default,
    fr: translation_json_2.default,
};
void i18next_1.default.use(i18next_browser_languagedetector_1.default).init({
    fallbackLng: 'en',
    debug: false,
    resources: {
        en: {
            translation: exports.locales.en,
        },
        fr: {
            translation: exports.locales.fr,
        },
    },
});
/**
 * Adds a locale to the available i18n bundles.
 *
 * @param name Name of the locale.
 * @param locale Contents of the locale.
 */
async function addLocale(name, locale) {
    i18next_1.default.addResourceBundle(name, 'translation', locale, true, true);
    await i18next_1.default.changeLanguage();
}
exports.addLocale = addLocale;
/**
 * Returns an internationalized version of a locale key.
 *
 * @param key A locale key.
 * @param options Data to pass to the i18n process.
 */
function t(key, options) {
    return i18next_1.default.t(key, options);
}
exports.t = t;
/**
 * Converts a type to a valid i18n key.
 *
 * @param key The key to convert.
 */
function toI18nKey(key) {
    return key.replace('_', '-');
}
exports.toI18nKey = toI18nKey;
/**
 * Returns an origin hint function based on rounds information.
 *
 * @param roundNumber Number of the round.
 * @param roundCount Count of rounds.
 * @param skipFirstRound Whether to skip the first round.
 * @param matchLocation Location of the match.
 */
function getOriginHint(roundNumber, roundCount, skipFirstRound, matchLocation) {
    if (roundNumber === 1) {
        if (matchLocation === 'single_bracket')
            return (position) => t('origin-hint.seed', { position });
        if (matchLocation === 'winner_bracket')
            return (position) => t('origin-hint.seed', { position });
        if (matchLocation === 'loser_bracket' && skipFirstRound)
            return (position) => t('origin-hint.seed', { position });
    }
    if ((0, helpers_1.isMajorRound)(roundNumber) && matchLocation === 'loser_bracket') {
        if (roundNumber === roundCount - 2)
            return (position) => t('origin-hint.winner-bracket-semi-final', { position });
        if (roundNumber === roundCount)
            return () => t('origin-hint.winner-bracket-final');
        const roundNumberWB = Math.ceil((roundNumber + 1) / 2);
        if (skipFirstRound)
            return (position) => t('origin-hint.winner-bracket', { round: roundNumberWB - 1, position });
        return (position) => t('origin-hint.winner-bracket', { round: roundNumberWB, position });
    }
    return undefined;
}
exports.getOriginHint = getOriginHint;
/**
 * Returns an origin hint function for a match in final.
 *
 * @param stageType Type of the stage.
 * @param finalType Type of the final.
 * @param roundNumber Number of the round.
 */
function getFinalOriginHint(stageType, finalType, roundNumber) {
    if (stageType === 'single_elimination')
        return (position) => t('origin-hint.consolation-final', { position });
    // Double elimination.
    if (finalType === 'grand_final') {
        return roundNumber === 1
            ? () => t('origin-hint.grand-final') // Grand Final round 1
            : undefined; // Grand Final round 2 (no hint because it's obvious both participants come from the previous round)
    }
    // Consolation final in double elimination.
    return (position) => position === 1
        ? t('origin-hint.double-elimination-consolation-final-opponent-1')
        : t('origin-hint.double-elimination-consolation-final-opponent-2');
}
exports.getFinalOriginHint = getFinalOriginHint;
/**
 * Returns the label of a match.
 *
 * @param matchNumber Number of the match.
 * @param roundNumber Number of the round.
 * @param roundCount Count of rounds.
 * @param matchLocation Location of the match.
 */
function getMatchLabel(matchNumber, roundNumber, roundCount, matchLocation) {
    if (roundNumber === undefined || roundCount === undefined || matchLocation === undefined)
        return t('match-label.default', { matchNumber });
    const matchPrefix = matchLocation === 'winner_bracket' ? t('match-label.winner-bracket') :
        matchLocation === 'loser_bracket' ? t('match-label.loser-bracket') : t('match-label.standard-bracket');
    const inSemiFinalRound = roundNumber === roundCount - 1;
    const inFinalRound = roundNumber === roundCount;
    if (matchLocation === 'single_bracket') {
        if (inSemiFinalRound)
            return t('match-label.standard-bracket-semi-final', { matchNumber });
        if (inFinalRound)
            return t('match-label.standard-bracket-final');
    }
    if (inSemiFinalRound)
        return t('match-label.double-elimination-semi-final', { matchPrefix, matchNumber });
    if (inFinalRound)
        return t('match-label.double-elimination-final', { matchPrefix });
    return t('match-label.double-elimination', { matchPrefix, roundNumber, matchNumber });
}
exports.getMatchLabel = getMatchLabel;
/**
 * Returns the label of a match in final.
 *
 * @param finalType Type of the final.
 * @param roundNumber Number of the round.
 * @param roundCount Count of rounds.
 */
function getFinalMatchLabel(finalType, roundNumber, roundCount) {
    // Single elimination.
    if (finalType === 'consolation_final')
        return t('match-label.consolation-final');
    // Double elimination.
    if (roundCount === 1)
        return t('match-label.grand-final-single');
    return t('match-label.grand-final', { roundNumber });
}
exports.getFinalMatchLabel = getFinalMatchLabel;
/**
 * Returns the status of a match.
 *
 * @param status The match status.
 */
function getMatchStatus(status) {
    switch (status) {
        case brackets_model_1.Status.Locked:
            return t('match-status.locked');
        case brackets_model_1.Status.Waiting:
            return t('match-status.waiting');
        case brackets_model_1.Status.Ready:
            return t('match-status.ready');
        case brackets_model_1.Status.Running:
            return t('match-status.running');
        case brackets_model_1.Status.Completed:
            return t('match-status.completed');
        case brackets_model_1.Status.Archived:
            return t('match-status.archived');
        default:
            return 'Unknown status';
    }
}
exports.getMatchStatus = getMatchStatus;
/**
 * Returns the name of a group.
 *
 * @param groupNumber Number of the group.
 */
function getGroupName(groupNumber) {
    return t('common.group-name', { groupNumber });
}
exports.getGroupName = getGroupName;
/**
 * Returns the name of the bracket.
 *
 * @param stage The current stage.
 * @param type Type of the bracket.
 */
function getBracketName(stage, type) {
    switch (type) {
        case 'winner_bracket':
        case 'loser_bracket':
            return t(`common.group-name-${toI18nKey(type)}`, { stage });
        default:
            return undefined;
    }
}
exports.getBracketName = getBracketName;
// eslint-disable-next-line jsdoc/require-param
/**
 * Returns the name of a round.
 */
function getRoundName({ roundNumber, roundCount }, t) {
    return roundNumber === roundCount ? t('common.round-name-final') : t('common.round-name', { roundNumber });
}
exports.getRoundName = getRoundName;
// eslint-disable-next-line jsdoc/require-param
/**
 * Returns the name of a round in the winner bracket of a double elimination stage.
 */
function getWinnerBracketRoundName({ roundNumber, roundCount }, t) {
    return roundNumber === roundCount ? t('common.round-name-winner-bracket-final') : t('common.round-name-winner-bracket', { roundNumber });
}
exports.getWinnerBracketRoundName = getWinnerBracketRoundName;
// eslint-disable-next-line jsdoc/require-param
/**
 * Returns the name of a round in the loser bracket of a double elimination stage.
 */
function getLoserBracketRoundName({ roundNumber, roundCount }, t) {
    return roundNumber === roundCount ? t('common.round-name-loser-bracket-final') : t('common.round-name-loser-bracket', { roundNumber });
}
exports.getLoserBracketRoundName = getLoserBracketRoundName;
